<template>
  <el-dialog
    class="translate-popup"
    :title="dialogTitle"
    :width="isTableStep ? '1024px' : '550px'"
    :visible="visible"
    :show-close="!processing"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    @close="$emit('close')"
  >
    <!-- STEP 0: API Key Input -->
    <div v-if="step === 0" style="padding: 10px 0;">
      <div style="background: #f0f9eb; padding: 10px; border-radius: 4px; margin-bottom: 15px; font-size: 13px;">
        <strong>Thêm dịch mới</strong> - Nếu chưa có Key hoặc muốn sử dụng tài khoản khác.
      </div>
      <el-select v-model="apiModel" placeholder="Chọn AI Model" style="margin-bottom: 10px;">
        <el-option label="Gemini" value="gemini"></el-option>
        <el-option label="DeepSeek" value="deepseek"></el-option>
      </el-select>
      <p style="font-size: 14px; color: #303133; margin-bottom: 15px;">
        Nhập API Key để sử dụng dịch tự động.
      </p>
      <el-input
        v-model="apiKeyInput"
        :placeholder="apiModel === 'gemini' ? 'Nhập Gemini API Key...' : 'Nhập DeepSeek API Key...'"
        show-password
        clearable
        style="margin-bottom: 10px;"
      ></el-input>
      <p style="font-size: 12px; color: #909399; margin: 0;">
        <i class="el-icon-info"></i>
        API Key sẽ được lưu trên trình duyệt của bạn.
      </p>
    </div>

    <!-- STEP 1: Select Range & Locales -->
    <el-form v-if="step === 1" label-position="top">
      <el-form-item label="Phạm vi dịch">
        <div class="w-full" style="display: flex; gap: 15px;">
          <div style="flex: 1;">
            <span style="font-size: 13px; color: #606266;">Từ dòng:</span>
            <el-input-number
              v-model="from"
              :min="1"
              :max="to"
              style="width: 100%; margin-top: 5px;"
            ></el-input-number>
          </div>
          <div style="flex: 1;">
            <span style="font-size: 13px; color: #606266;">Đến dòng:</span>
            <el-input-number
              v-model="to"
              :min="from"
              :max="total"
              style="width: 100%; margin-top: 5px;"
            ></el-input-number>
          </div>
        </div>
      </el-form-item>

      <el-form-item label="Chọn ngôn ngữ dịch">
        <el-checkbox-group v-model="selectedLocales" class="locales-group">
          <el-checkbox
            v-for="item in locales"
            :key="item.code"
            :label="item.code"
            class="locale-checkbox"
          >
            <div class="locale-checkbox-content">
              <img
                :src="require(`@/assets/images/${item.code}.png`)"
                class="locale-flag"
                alt=""
              />
              <span class="locale-name">{{ item.name || item.code }}</span>
            </div>
          </el-checkbox>
        </el-checkbox-group>
      </el-form-item>

      <div v-if="fetching" class="mt-4">
        <p style="font-size: 13px; color: #606266; margin-bottom: 5px;">Đang lấy dữ liệu từ hệ thống...</p>
        <el-progress
          :percentage="fetchingPercentage"
          :stroke-width="12"
        ></el-progress>
      </div>
    </el-form>

    <!-- STEP 2: Selection & Translation -->
    <div v-else-if="step === 2">
      <div v-if="!translating">
        <el-row
          class="mt-0 mb-2"
          type="flex"
          justify="space-between"
          align="middle"
        >
          <h3 class="text-center mt-0" style="margin: 0;">{{ entityTitle }} ({{ checkedTranslateData.length }})</h3>
          <el-input
            v-model="searchText"
            class="w-50"
            placeholder="Nhập thông tin để tìm kiếm"
            style="width: 250px;"
            @input="() => selectedRows = []"
          ></el-input>
        </el-row>
        <el-table
          :data="filteredTranslateData.map((d, index) => ({ ...d, stt: index + 1 }))"
          style="width: 100%"
          height="400"
        >
          <el-table-column
            key="checkbox"
            label=""
            width="50"
            fixed="left"
          >
            <template slot-scope="scope">
              <el-checkbox
                :value="selectedRows.includes(scope.row.stt)"
                @change="(v) => handleSelectRow(scope.row.stt, v)"
              ></el-checkbox>
            </template>
          </el-table-column>
          <el-table-column
            v-for="column in [
              {
                key: 'stt',
                label: 'STT',
                fixed: 'left'
              },
              ...excelColumns
            ]"
            :key="column.key"
            :prop="column.key"
            :label="column.label"
            :min-width="column.width"
            :fixed="column.fixed"
          >
            <template slot-scope="scope">
              <div class="text-limited text-limited__2" :title="scope.row[column.key]">
                {{ column.locale ? (scope.row[column.locale] ? scope.row[column.locale][column.column_key] : '') : scope.row[column.key] }}
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div v-else class="text-center" style="padding: 20px 0;">
        <p style="font-size: 14px; color: #303133; font-weight: bold; margin-bottom: 15px;">
          Đang dịch tự động qua AI API ({{ translatedCount }}/{{ checkedTranslateData.length }} dòng)...
        </p>
        <el-progress
          :percentage="translatingPercentage"
          :stroke-width="12"
        ></el-progress>
      </div>
    </div>

    <!-- STEP 3: Review Translated Data -->
    <div v-else-if="step === 3">
      <el-row
        class="mt-0 mb-2"
        type="flex"
        justify="space-between"
        align="middle"
      >
        <div style="display: flex; align-items: center; gap: 10px;">
          <h3 class="text-center mt-0" style="margin: 0;">Bản ghi đã dịch ({{ translatedItems.length }})</h3>
          <span v-if="reviewStats.errorCount > 0" style="color: #f56c6c; font-size: 13px; font-weight: bold;">
            <i class="el-icon-error"></i> {{ reviewStats.errorCount }} bài lỗi
          </span>
          <span v-if="reviewStats.warningCount > 0" style="color: #e6a23c; font-size: 13px; font-weight: bold;">
            <i class="el-icon-warning"></i> {{ reviewStats.warningCount }} bài chưa dịch xong
          </span>
        </div>
        <el-input
          v-model="reviewSearchText"
          class="w-50"
          placeholder="Nhập thông tin để tìm kiếm"
          style="width: 250px;"
        ></el-input>
      </el-row>
      <el-table
        :data="filteredReviewData.map((d, index) => ({ ...d, stt: index + 1 }))"
        style="width: 100%"
        height="400"
        :row-class-name="tableRowClassName"
      >
        <el-table-column
          v-for="column in [
            {
              key: 'stt',
              label: 'STT',
              fixed: 'left'
            },
            ...excelColumns
          ]"
          :key="column.key"
          :prop="column.key"
          :label="column.label"
          :min-width="column.width"
          :fixed="column.fixed"
        >
          <template slot-scope="scope">
            <div
              class="cell-wrapper"
              :class="getCellStatusClass(scope.row, column)"
              :title="getCellTooltip(scope.row, column)"
            >
              <i v-if="getCellStatus(scope.row, column) === 'error'" class="el-icon-error status-icon"></i>
              <i v-else-if="getCellStatus(scope.row, column) === 'warning'" class="el-icon-warning-outline status-icon"></i>
              <div class="text-limited text-limited__2">
                {{ getCellValue(scope.row, column) }}
              </div>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- STEP 4: Save / Update -->
    <div v-else-if="step === 4" style="word-break: break-word;">
      <div v-if="!saving">
        <p style="font-weight: bold; font-size: 15px; color: #303133;">Dịch hoàn tất!</p>
        <p>Số lượng bản dịch thành công: {{ translatedItems.length }} / {{ checkedTranslateData.length }}</p>
        <p>* Lưu ý: Toàn bộ dữ liệu dịch sẽ được cập nhật trực tiếp vào hệ thống dưới các ngôn ngữ tương ứng đã chọn.</p>
        <p>Bấm <strong>Xác nhận</strong> để cập nhật dữ liệu.</p>
      </div>
      <div v-else class="text-center" style="padding: 20px 0;">
        <p style="font-size: 14px; color: #303133; font-weight: bold; margin-bottom: 15px;">
          Đang lưu dữ liệu dịch vào hệ thống ({{ savedCount }}/{{ translatedItems.length }} dòng)...
        </p>
        <el-progress
          :percentage="savingPercentage"
          :stroke-width="12"
        ></el-progress>
      </div>
    </div>

    <!-- FOOTER ACTIONS -->
    <span slot="footer">
      <el-row type="flex" align="middle" justify="end">
        <!-- Back button (only when not translating/saving) -->
        <el-button
          v-if="step > 1 && !processing"
          type="info"
          class="mr-2"
          @click="handleBackStep"
        >
          Quay lại
        </el-button>

        <!-- Step 0 action -->
        <el-button
          v-if="step === 0"
          type="primary"
          :disabled="!apiKeyInput || apiKeyInput.trim() === ''"
          @click="saveApiKey"
        >
          Lưu & Tiếp tục
        </el-button>

        <!-- Step 1: Change API Key button -->
        <el-button
          v-if="step === 1 && !processing"
          type="warning"
          plain
          class="mr-2"
          @click="step = 0"
        >
          <i class="el-icon-key"></i> Đổi API Key
        </el-button>

        <!-- Step 1 action -->
        <el-button
          v-if="step === 1"
          type="primary"
          :loading="fetching"
          :disabled="selectedLocales.length === 0"
          @click="fetchDataToTranslate"
        >
          Tiếp tục
        </el-button>

        <!-- Step 2 action -->
        <el-button
          v-if="step === 2 && !translating"
          type="primary"
          :disabled="checkedTranslateData.length === 0"
          @click="executeTranslation"
        >
          Bắt đầu dịch
        </el-button>

        <!-- Step 3 action -->
        <el-button
          v-if="step === 3"
          type="primary"
          @click="() => { step = 4 }"
        >
          Tiếp tục
        </el-button>

        <!-- Step 4 action -->
        <el-button
          v-if="step === 4 && !saving"
          type="primary"
          @click="saveTranslatedData"
        >
          Xác nhận
        </el-button>
      </el-row>
    </span>
  </el-dialog>
</template>

<script>
import dayjs from 'dayjs'
import { mapGetters, mapActions } from 'vuex'
import { listPlaces, updatePlace } from '@/services/place'
import { getComments } from '@/services/comment'
import { getCategory, updateCategory } from '@/services/category'
import { getArtifacts, updateArtifact } from '@/services/artifact'
import { getTourDays, updateSchedule } from '@/services/tour'
import { listStorages, updateStorage } from '@/services/storage'
import { getRoomTypes, updateRoomType } from '@/services/room-type'
import { getTags, updateTag } from '@/services/tag'
import { getPlaceServices, updatePlaceService } from '@/services/place/service'
import { getPlaceFacilities, updatePlaceFacility } from '@/services/place/facility'
import { getPlaceFacilityGroups, updatePlaceFacilityGroup } from '@/services/place/facility-group'
import { convertData as convertExportData } from '@/utils/export'
import { convertData as convertImportData } from '@/utils/import'
import {
  translateItem,
  getApiModel,
  setApiModel,
  getApiKey,
  setApiKey
} from '@/services/translate'

export default {
  name: 'TranslatePopup',
  props: {
    visible: Boolean,
    order: String,
    total: Number,
    columns: Array,
    type: String,
    titles: Array,
    detail: Object,
    children: Boolean,
    activeTab: [String, Boolean]
  },
  data() {
    return {
      apiModel: getApiModel() || 'gemini',
      step: getApiModel() ? (getApiKey(getApiModel()) ? 1 : 0) : 0,
      apiKeyInput: getApiKey(getApiModel()),
      from: 1,
      to: this.total || 1,
      selectedLocales: [],
      rawFetchedData: [],
      selectedRows: [],
      searchText: '',

      // Loading states
      fetching: false,
      fetchedCount: 0,
      fetchingSize: 100,

      translating: false,
      translatedCount: 0,
      translatedItems: [],

      saving: false,
      savedCount: 0,

      allTags: [],
      reviewSearchText: ''
    }
  },
  computed: {
    ...mapGetters(['language', 'constant']),
    dialogTitle() {
      if (this.step === 0) return 'Dịch tự động dữ liệu (Cấu hình API Key)'
      if (this.step === 1) return 'Dịch tự động dữ liệu (Bước 1: Cấu hình)'
      if (this.step === 2) return 'Dịch tự động dữ liệu (Bước 2: Chọn bản ghi)'
      if (this.step === 3) return 'Dịch tự động dữ liệu (Bước 3: Kiểm tra kết quả dịch)'
      return 'Dịch tự động dữ liệu (Bước 4: Lưu kết quả)'
    },
    isTableStep() {
      return (this.step === 2 && !this.translating) || this.step === 3
    },
    processing() {
      return this.fetching || this.translating || this.saving
    },
    locales() {
      return this.constant.constants.locales.filter((l) => l.code !== 'vi')
    },
    entityTitle() {
      const selectedTitle = this.titles.find((t) => t.value === this.activeTab) || { name: '', label: '' }
      if (this.type === 'room_type') {
        return 'Hạng phòng'
      }
      if (this.type === 'tour_day') {
        return 'Lịch trình'
      }
      if (this.type === 'artifact') {
        return 'Danh sách'
      }
      if (this.type === 'category') {
        return 'Danh mục ' + (selectedTitle.name || selectedTitle.label)
      }
      return selectedTitle.label || selectedTitle.name
    },
    pageSize() {
      return Math.min(this.to, this.fetchingSize)
    },
    limit() {
      return this.to - this.from + 1
    },
    startPage() {
      return Math.floor(((this.from - 1) / this.pageSize)) + 1
    },
    endPage() {
      return Math.floor(((this.to - 1) / this.pageSize)) + 1
    },
    fetchingPercentage() {
      if (this.limit > 0) {
        const result = Math.ceil((this.fetchedCount / this.limit * 100))
        return result > 100 ? 100 : result
      }
      return 0
    },
    translatingPercentage() {
      if (this.checkedTranslateData.length > 0) {
        return Math.ceil((this.translatedCount / this.checkedTranslateData.length * 100))
      }
      return 0
    },
    savingPercentage() {
      if (this.translatedItems.length > 0) {
        return Math.ceil((this.savedCount / this.translatedItems.length * 100))
      }
      return 0
    },
    requestParams() {
      let params = {
        locale: 'vi',
        order: this.order
      }
      if (['image', 'audio', 'video', 'tour_day', 'artifact', 'room_type'].includes(this.type)) {
        if (['image', 'audio', 'video'].includes(this.type)) {
          params = { ...params, file_type: this.type }
        }
        if (this.detail) {
          params = { ...params, place_id: this.detail.id }
        }
      } else if (['category', 'place-services', 'place-facilities'].includes(this.type)) {
        params = { ...params, place_type: this.activeTab }
      } else if (this.type === 'comment') {
        params = { ...params, approved: this.activeTab }
      } else if (['tags', 'place-facility-groups'].includes(this.type)) {
        // No additional parameters
      } else {
        params = { ...params, place_types: this.type }
        if (!this.children) {
          params = { ...params, only_parent: true }
        } else {
          params = { ...params, only_children: true }
          if (this.detail && this.detail.type === this.type) {
            params = { ...params, parent_id: this.detail.id }
          }
        }
        if (this.detail && this.detail.type !== this.type) {
          params = { ...params, [`${this.detail.type}_id`]: this.detail.id }
        }
      }
      return params
    },
    excelColumns() {
      const languageColumns = this.columns.filter((c) => c.language && !(c.languageExcludeTypes || []).includes(this.type))
      const lColumns = this.selectedLocales.map((localeCode) => {
        const l = this.locales.find((loc) => loc.code === localeCode)
        const labelSuffix = l ? l.code : localeCode
        return languageColumns.map((column) => ({
          ...column,
          locale: localeCode,
          column_key: column.key,
          key: `${column.key}__${localeCode}`,
          label: `${column.label} (${labelSuffix})`
        }))
      }).flat()
      return [...this.columns, ...lColumns].map((c) => ({ ...c, label: c.label.toUpperCase(), field: c.key }))
    },
    formattedData() {
      // Formats the fetched raw data to the structure expected by translation and Excel columns
      const languageColumns = this.columns.filter((c) => c.language && !(c.languageExcludeTypes || []).includes(this.type))
      return this.rawFetchedData.map(d => {
        const data = {}
        this.columns.forEach((column) => {
          data[`${column.key}`] = d[column.key]
        })
        this.selectedLocales.forEach(localeCode => {
          data[localeCode] = {}
          languageColumns.forEach((lColumn) => {
            data[localeCode][`${lColumn.key}`] = d[localeCode] ? d[localeCode][lColumn.key] || '' : ''
          })
        })
        return data
      })
    },
    filteredTranslateData() {
      return this.formattedData.filter((d) => {
        if (this.searchText) {
          let result = false
          const keys = Object.keys(d)
          for (let i = 0; i < keys.length; i++) {
            if (d[keys[i]]) {
              if (typeof d[keys[i]] !== 'object') {
                const value = d[keys[i]].toString()
                if (value.toLowerCase().includes(this.searchText.toLowerCase())) {
                  result = true
                }
              } else {
                const oKeys = Object.keys(d[keys[i]])
                for (let j = 0; j < oKeys.length; j++) {
                  if (d[keys[i]][oKeys[j]]) {
                    if (typeof d[keys[i]][oKeys[j]] !== 'object') {
                      const value = d[keys[i]][oKeys[j]].toString()
                      if (value.toLowerCase().includes(this.searchText.toLowerCase())) {
                        result = true
                      }
                    }
                  }
                }
              }
            }
            if (result) {
              break
            }
          }
          return result
        }
        return true
      })
    },
    filteredReviewData() {
      return this.translatedItems.filter((d) => {
        if (this.reviewSearchText) {
          let result = false
          const keys = Object.keys(d)
          for (let i = 0; i < keys.length; i++) {
            if (d[keys[i]]) {
              if (typeof d[keys[i]] !== 'object') {
                const value = d[keys[i]].toString()
                if (value.toLowerCase().includes(this.reviewSearchText.toLowerCase())) {
                  result = true
                }
              } else {
                const oKeys = Object.keys(d[keys[i]])
                for (let j = 0; j < oKeys.length; j++) {
                  if (d[keys[i]][oKeys[j]]) {
                    if (typeof d[keys[i]][oKeys[j]] !== 'object') {
                      const value = d[keys[i]][oKeys[j]].toString()
                      if (value.toLowerCase().includes(this.reviewSearchText.toLowerCase())) {
                        result = true
                      }
                    }
                  }
                }
              }
            }
            if (result) {
              break
            }
          }
          return result
        }
        return true
      })
    },
    checkedTranslateData() {
      if (this.selectedRows.length > 0) {
        return this.filteredTranslateData.filter((d, index) => this.selectedRows.includes(index + 1))
      } else {
        return this.filteredTranslateData
      }
    },
    payloadData() {
      return this.translatedItems.map((d) => {
        let vi = convertImportData(d, this.columns, { ...this.constant, tags: this.allTags }, this.type)
        if (this.detail && this.detail.id && !['category', 'artifact', 'room_type', 'comment', 'tour_day', 'video', 'image', 'audio', 'tags', 'place-services', 'place-facilities', 'place-facility-groups'].includes(this.type)) {
          if (this.children && this.detail.type === this.type) {
            vi = { ...vi, parent_id: this.detail.id }
          } else {
            vi = { ...vi, [`${this.detail.type}_id`]: this.detail.id }
          }
        }
        const data = { vi }
        this.selectedLocales.forEach(localeCode => {
          data[localeCode] = {
            ...vi,
            ...d[localeCode]
          }
        })
        return data
      })
    },
    reviewStats() {
      let errorCount = 0
      let warningCount = 0
      this.translatedItems.forEach(row => {
        if (row._hasError) {
          errorCount++
        } else if (this.hasRowWarning(row)) {
          warningCount++
        }
      })
      return { errorCount, warningCount }
    }
  },
  beforeMount() {
    this.loadAllTags()
    // Select all locales by default
    this.selectedLocales = this.locales.map(l => l.code)
  },
  methods: {
    ...mapActions('public', ['updateReloadConstant']),
    hasRowWarning(row) {
      if (row._hasError) return false
      const languageColumns = this.columns.filter((c) => c.language && !(c.languageExcludeTypes || []).includes(this.type))
      return this.selectedLocales.some((localeCode) => {
        return languageColumns.some((col) => {
          const origVal = row[col.key]
          const transVal = row[localeCode] ? row[localeCode][col.key] : ''
          return typeof origVal === 'string' && origVal.trim() !== '' && (!transVal || typeof transVal !== 'string' || transVal.trim() === '')
        })
      })
    },
    tableRowClassName({ row }) {
      if (row._hasError) {
        return 'row-status-error'
      }
      if (this.hasRowWarning(row)) {
        return 'row-status-warning'
      }
      return ''
    },
    getCellStatus(row, column) {
      if (row._hasError) {
        return 'error'
      }
      if (column.locale) {
        const origVal = row[column.column_key]
        const transVal = row[column.locale] ? row[column.locale][column.column_key] : ''
        if (typeof origVal === 'string' && origVal.trim() !== '' && (!transVal || typeof transVal !== 'string' || transVal.trim() === '')) {
          return 'warning'
        }
      }
      return 'normal'
    },
    getCellStatusClass(row, column) {
      const status = this.getCellStatus(row, column)
      if (status === 'error') return 'cell-status-error'
      if (status === 'warning') return 'cell-status-warning'
      return ''
    },
    getCellValue(row, column) {
      if (column.locale) {
        const val = row[column.locale] ? row[column.locale][column.column_key] : ''
        const origVal = row[column.column_key]
        if ((!val || typeof val !== 'string' || val.trim() === '') && typeof origVal === 'string' && origVal.trim() !== '') {
          return '(Chưa dịch)'
        }
        return val
      }
      return row[column.key]
    },
    getCellTooltip(row, column) {
      if (row._hasError) {
        return row._errorMessage || 'Lỗi gọi API dịch'
      }
      if (column.locale) {
        const val = row[column.locale] ? row[column.locale][column.column_key] : ''
        const origVal = row[column.column_key]
        if ((!val || typeof val !== 'string' || val.trim() === '') && typeof origVal === 'string' && origVal.trim() !== '') {
          return 'Cột này chưa dịch được'
        }
        return val
      }
      return row[column.key]
    },
    loadAllTags() {
      getTags({ page: 1, per_page: 100, locale: 'vi' }).then((response) => {
        this.allTags = response.result
      }).catch(() => {
        this.allTags = []
      })
    },
    handleSelectRow(stt, isChecked) {
      if (isChecked) {
        this.selectedRows = [...this.selectedRows, stt]
      } else {
        this.selectedRows = this.selectedRows.filter((r) => r !== stt)
      }
    },
    saveApiKey() {
      if (this.apiKeyInput && this.apiKeyInput.trim()) {
        setApiModel(this.apiModel)
        setApiKey(this.apiModel, this.apiKeyInput.trim())
        this.step = 1
      }
    },
    handleBackStep() {
      if (this.step > 1) {
        this.step -= 1
        if (this.step === 1) {
          this.rawFetchedData = []
          this.selectedRows = []
          this.translatedItems = []
        } else if (this.step === 2) {
          this.translatedItems = []
        }
      }
    },

    // Step 1 -> Step 2: Fetch data from system
    async fetchDataToTranslate() {
      this.rawFetchedData = []
      this.selectedRows = []
      let fetched = []
      this.fetching = true
      this.fetchedCount = 0

      let page = this.startPage
      let params = this.requestParams
      try {
        while (page <= this.endPage) {
          params = {
            ...params,
            page,
            per_page: this.pageSize
          }
          let request = null
          if (this.type === 'comment') {
            request = getComments(params)
          } else if (this.type === 'category') {
            request = this.handleListCategories(params)
          } else if (this.type === 'tags') {
            request = this.handleListTags(params)
          } else if (this.type === 'place-services') {
            request = this.handleListPlaceServices(params)
          } else if (this.type === 'place-facilities') {
            request = this.handleListPlaceFacilities(params)
          } else if (this.type === 'place-facility-groups') {
            request = this.handleListPlaceFacilityGroups(params)
          } else if (this.type === 'artifact') {
            request = this.handleListArtifacts(params)
          } else if (this.type === 'room_type') {
            request = this.handleListRoomTypes(params)
          } else if (this.type === 'tour_day') {
            request = this.handleListTourDays(params)
          } else if (['image', 'audio', 'video'].includes(this.type)) {
            request = this.handleListStorages(params)
          } else {
            request = this.handleListPlaces(params)
          }

          const response = await request
          fetched = [...fetched, ...response.result]
          this.fetchedCount = fetched.length
          page = page + 1
        }

        const offset = this.from - (this.pageSize * (this.startPage - 1)) - 1
        this.rawFetchedData = fetched.slice(
          offset > 0 ? offset : 0,
          this.limit + offset
        )

        this.step = 2
      } catch (err) {
        console.error(err)
        this.$message.error('Lỗi khi tải dữ liệu từ hệ thống!')
      } finally {
        this.fetching = false
      }
    },

    // Helper functions for custom list fetches
    async handleListPlaces(params) {
      return await listPlaces(params).then(async (response) => {
        const requests = this.selectedLocales.map((lCode) => {
          return listPlaces({ ...params, locale: lCode })
        })
        const langData = await Promise.all(requests)
        response.result = response.result.map((r, index) => {
          const result = convertExportData(r, this.columns, this.constant)
          this.selectedLocales.forEach((localeCode, i) => {
            result[localeCode] = convertExportData(langData[i].result[index], this.columns, this.constant)
          })
          return result
        })
        return response
      }).catch(() => {
        return { result: [] }
      })
    },
    async handleListCategories(params) {
      return await getCategory(params, this.activeTab).then(async (response) => {
        response.result = response.result.map((r, index) => {
          const translations = r.translations
          const viData = translations.find((t) => t.locale === 'vi') || { name: '' }
          const result = convertExportData({ id: r.id, name: viData.name }, this.columns, this.constant)
          this.selectedLocales.forEach((localeCode) => {
            const localeData = translations.find((t) => t.locale === localeCode) || { name: '' }
            result[localeCode] = convertExportData({ id: r.id, name: localeData.name }, this.columns, this.constant)
          })
          return result
        })
        return response
      }).catch(() => {
        return { result: [] }
      })
    },
    getLocaleValue(item, localeCode, key) {
      if (!item) return ''
      // 1. Ưu tiên lấy theo item.key trước
      if (item[key]) {
        return item[key]
      }
      // 2. Nếu không có thì lấy theo item.translation
      if (item.translation && item.translation[key]) {
        return item.translation[key]
      }
      // 3. Nếu không có thì lấy theo item.translations
      if (item.translations && Array.isArray(item.translations)) {
        const trans = item.translations.find((t) => t.locale === localeCode)
        if (trans && trans[key]) return trans[key]
      }
      // 4. Nếu không thấy thì để là '' luôn
      return ''
    },
    async handleListTags(params) {
      return await getTags(params).then(async (response) => {
        const requests = this.selectedLocales.map((lCode) => {
          return getTags({ ...params, locale: lCode })
        })
        const langData = await Promise.all(requests)
        response.result = (response.result || []).map((r, index) => {
          const viData = {
            ...r,
            name: this.getLocaleValue(r, 'vi', 'name'),
            description: this.getLocaleValue(r, 'vi', 'description')
          }
          const result = convertExportData(viData, this.columns, this.constant)
          this.selectedLocales.forEach((localeCode, i) => {
            const lItem = langData[i] && langData[i].result ? langData[i].result[index] : null
            const lData = {
              ...r,
              name: this.getLocaleValue(lItem, localeCode, 'name'),
              description: this.getLocaleValue(lItem, localeCode, 'description')
            }
            result[localeCode] = convertExportData(lData, this.columns, this.constant)
          })
          return result
        })
        return response
      }).catch(() => {
        return { result: [] }
      })
    },
    async handleListPlaceServices(params) {
      return await getPlaceServices(params).then(async (response) => {
        const requests = this.selectedLocales.map((lCode) => {
          return getPlaceServices({ ...params, locale: lCode })
        })
        const langData = await Promise.all(requests)
        response.result = (response.result || []).map((r, index) => {
          const viData = {
            ...r,
            name: this.getLocaleValue(r, 'vi', 'name')
          }
          const result = convertExportData(viData, this.columns, this.constant)
          this.selectedLocales.forEach((localeCode, i) => {
            const lItem = langData[i] && langData[i].result ? langData[i].result[index] : null
            const lData = {
              ...r,
              name: this.getLocaleValue(lItem, localeCode, 'name')
            }
            result[localeCode] = convertExportData(lData, this.columns, this.constant)
          })
          return result
        })
        return response
      }).catch(() => {
        return { result: [] }
      })
    },
    async handleListPlaceFacilities(params) {
      return await getPlaceFacilities(params).then(async (response) => {
        const requests = this.selectedLocales.map((lCode) => {
          return getPlaceFacilities({ ...params, locale: lCode })
        })
        const langData = await Promise.all(requests)
        response.result = (response.result || []).map((r, index) => {
          const viData = {
            ...r,
            name: this.getLocaleValue(r, 'vi', 'name')
          }
          const result = convertExportData(viData, this.columns, this.constant)
          this.selectedLocales.forEach((localeCode, i) => {
            const lItem = langData[i] && langData[i].result ? langData[i].result[index] : null
            const lData = {
              ...r,
              name: this.getLocaleValue(lItem, localeCode, 'name')
            }
            result[localeCode] = convertExportData(lData, this.columns, this.constant)
          })
          return result
        })
        return response
      }).catch(() => {
        return { result: [] }
      })
    },
    async handleListPlaceFacilityGroups(params) {
      return await getPlaceFacilityGroups(params).then(async (response) => {
        const requests = this.selectedLocales.map((lCode) => {
          return getPlaceFacilityGroups({ ...params, locale: lCode })
        })
        const langData = await Promise.all(requests)
        response.result = (response.result || []).map((r, index) => {
          const viData = {
            ...r,
            name: this.getLocaleValue(r, 'vi', 'name')
          }
          const result = convertExportData(viData, this.columns, this.constant)
          this.selectedLocales.forEach((localeCode, i) => {
            const lItem = langData[i] && langData[i].result ? langData[i].result[index] : null
            const lData = {
              ...r,
              name: this.getLocaleValue(lItem, localeCode, 'name')
            }
            result[localeCode] = convertExportData(lData, this.columns, this.constant)
          })
          return result
        })
        return response
      }).catch(() => {
        return { result: [] }
      })
    },
    async handleListArtifacts(params) {
      return await getArtifacts(params).then(async (response) => {
        const requests = this.selectedLocales.map((lCode) => {
          return getArtifacts({ ...params, locale: lCode })
        })
        const langData = await Promise.all(requests)
        response.result = response.result.map((r, index) => {
          const newR = {
            ...r,
            vrtour: (r.translation ? r.translation.vrtour : r.vrtour) || ''
          }
          const result = convertExportData(newR, this.columns, this.constant)
          this.selectedLocales.forEach((localeCode, i) => {
            const lData = langData[i].result[index]
            const newLangData = {
              ...lData,
              vrtour: (lData.translation ? lData.translation.vrtour : lData.vrtour) || ''
            }
            result[localeCode] = convertExportData(newLangData, this.columns, this.constant)
          })
          return result
        })
        return response
      }).catch(() => {
        return { result: [] }
      })
    },
    async handleListTourDays(params) {
      return await getTourDays(params).then(async (response) => {
        const requests = this.selectedLocales.map((lCode) => {
          return getTourDays({ ...params, locale: lCode })
        })
        const langData = await Promise.all(requests)
        response.result = response.result.map((r, index) => {
          const result = convertExportData(r, this.columns, this.constant)
          this.selectedLocales.forEach((localeCode, i) => {
            const lData = langData[i].result[index]
            result[localeCode] = convertExportData(lData, this.columns, this.constant)
          })
          return result
        })
        return response
      }).catch(() => {
        return { result: [] }
      })
    },
    async handleListStorages(params) {
      return await listStorages(params).then(async (response) => {
        response.result = response.result.map((r) => {
          if (this.type === 'video') {
            const translations = r.translations
            const viData = translations.find((t) => t.locale === 'vi') || { title: '', description: '' }
            const result = convertExportData({ id: r.id, title: viData.title, description: viData.description }, this.columns, this.constant)
            this.selectedLocales.forEach((localeCode) => {
              const localeData = translations.find((t) => t.locale === localeCode) || { title: '', description: '' }
              result[localeCode] = convertExportData({ id: r.id, title: localeData.title, description: localeData.description }, this.columns, this.constant)
            })
            return result
          }
          return convertExportData(r, this.columns, this.constant)
        })
        return response
      }).catch(() => {
        return { result: [] }
      })
    },
    async handleListRoomTypes(params) {
      return await getRoomTypes(params).then(async (response) => {
        const requests = this.selectedLocales.map((lCode) => {
          return getRoomTypes({ ...params, locale: lCode })
        })
        const langData = await Promise.all(requests)
        response.result = response.result.map((r, index) => {
          const result = convertExportData(r, this.columns, this.constant)
          this.selectedLocales.forEach((localeCode, i) => {
            result[localeCode] = convertExportData(langData[i].result[index], this.columns, this.constant)
          })
          return result
        })
        return response
      }).catch(() => {
        return { result: [] }
      })
    },

    // Step 2 -> Step 3: Run Translation service (translate one item at a time)
    async executeTranslation() {
      this.translating = true
      this.translatedCount = 0
      this.translatedItems = []

      const items = this.checkedTranslateData
      try {
        for (let i = 0; i < items.length; i++) {
          const result = await translateItem(
            this.apiModel,
            items[i],
            this.selectedLocales,
            this.columns,
            this.type
          )
          this.translatedItems.push(result)
          this.translatedCount = i + 1
        }
        this.step = 3
      } catch (err) {
        console.error(err)
        this.$message.error(err.message || 'Lỗi khi gọi API dịch tự động!')
      } finally {
        this.translating = false
      }
    },

    // Step 3 -> Finish: Save to database
    async saveTranslatedData() {
      this.saving = true
      this.savedCount = 0

      try {
        let i = 0
        const importSize = 10
        const payloads = this.payloadData

        while (i < payloads.length) {
          const chunk = payloads.slice(i, i + importSize)
          const requests = chunk.map((p) => {
            if (this.type === 'category') {
              return this.handleUpdateCategory(p)
            }
            if (this.type === 'tags') {
              return this.handleUpdateTag(p)
            }
            if (this.type === 'place-services') {
              return this.handleUpdatePlaceService(p)
            }
            if (this.type === 'place-facilities') {
              return this.handleUpdatePlaceFacility(p)
            }
            if (this.type === 'place-facility-groups') {
              return this.handleUpdatePlaceFacilityGroup(p)
            }
            if (this.type === 'artifact') {
              return this.handleUpdateArtifact(p)
            }
            if (this.type === 'tour_day') {
              return this.handleUpdateSchedule(p)
            }
            if (this.type === 'room_type') {
              return this.handleUpdateRoomType(p)
            }
            if (['image', 'audio', 'video'].includes(this.type)) {
              return this.handleUpdateStorage(p)
            }
            return this.handleUpdatePlace(p)
          })

          await Promise.all(requests)
          i = i + importSize
          this.savedCount = i > payloads.length ? payloads.length : i
        }

        this.handleReloadConstant()
        this.$emit('reload')
        this.$emit('close')
        this.$notify.success({
          title: 'Thông báo !',
          message: 'Dịch và cập nhật dữ liệu thành công !'
        })
      } catch (err) {
        console.error(err)
        this.$message.error('Lỗi khi cập nhật dữ liệu dịch vào hệ thống!')
      } finally {
        this.saving = false
      }
    },

    // Update APIs
    async handleUpdateTag(payload) {
      try {
        const targetLocales = ['vi', ...this.selectedLocales]
        const requests = targetLocales.map((lCode) => {
          try {
            if (!payload[lCode]) return () => {}
            return updateTag({ locale: lCode, ...payload[lCode] })
          } catch (error) {
            return () => {}
          }
        })
        await Promise.all(requests)
      } catch (error) {
        //
      }
    },
    async handleUpdatePlaceService(payload) {
      try {
        const targetLocales = ['vi', ...this.selectedLocales]
        const requests = targetLocales.map((lCode) => {
          try {
            if (!payload[lCode]) return () => {}
            return updatePlaceService({ locale: lCode, ...payload[lCode] })
          } catch (error) {
            return () => {}
          }
        })
        await Promise.all(requests)
      } catch (error) {
        //
      }
    },
    async handleUpdatePlaceFacility(payload) {
      try {
        const targetLocales = ['vi', ...this.selectedLocales]
        const requests = targetLocales.map((lCode) => {
          try {
            if (!payload[lCode]) return () => {}
            return updatePlaceFacility({ locale: lCode, ...payload[lCode] })
          } catch (error) {
            return () => {}
          }
        })
        await Promise.all(requests)
      } catch (error) {
        //
      }
    },
    async handleUpdatePlaceFacilityGroup(payload) {
      try {
        const targetLocales = ['vi', ...this.selectedLocales]
        const requests = targetLocales.map((lCode) => {
          try {
            if (!payload[lCode]) return () => {}
            return updatePlaceFacilityGroup({ locale: lCode, ...payload[lCode] })
          } catch (error) {
            return () => {}
          }
        })
        await Promise.all(requests)
      } catch (error) {
        //
      }
    },
    async handleUpdatePlace(payload) {
      try {
        const targetLocales = ['vi', ...this.selectedLocales]
        const requests = targetLocales.map((lCode) => {
          try {
            if (!payload[lCode]) return () => {}
            return updatePlace({ locale: lCode, ...payload[lCode] })
          } catch (error) {
            return () => {}
          }
        })
        await Promise.all(requests)
      } catch (error) {
        //
      }
    },
    async handleUpdateCategory(payload) {
      try {
        const targetLocales = ['vi', ...this.selectedLocales]
        const requests = targetLocales.map((lCode) => {
          try {
            if (!payload[lCode]) return () => {}
            return updateCategory({ locale: lCode, ...payload[lCode] })
          } catch (error) {
            return () => {}
          }
        })
        await Promise.all(requests)
      } catch (error) {
        //
      }
    },
    async handleUpdateArtifact(payload) {
      try {
        const targetLocales = ['vi', ...this.selectedLocales]
        const requests = targetLocales.map((lCode) => {
          try {
            if (!payload[lCode]) return () => {}
            return updateArtifact({ locale: lCode, ...payload[lCode], place_id: this.detail ? this.detail.id : payload.place_id })
          } catch (error) {
            return () => {}
          }
        })
        await Promise.all(requests)
      } catch (error) {
        //
      }
    },
    async handleUpdateSchedule(payload) {
      try {
        const targetLocales = ['vi', ...this.selectedLocales]
        const requests = targetLocales.map((lCode) => {
          try {
            if (!payload[lCode]) return () => {}
            return updateSchedule({ locale: lCode, ...payload[lCode], place_id: this.detail ? this.detail.id : payload.place_id })
          } catch (error) {
            return () => {}
          }
        })
        await Promise.all(requests)
      } catch (error) {
        //
      }
    },
    async handleUpdateStorage(payload) {
      try {
        const targetLocales = ['vi', ...this.selectedLocales]
        const requests = targetLocales.map((lCode) => {
          if (this.type !== 'video' && lCode !== 'vi') {
            return
          }
          try {
            if (!payload[lCode]) return () => {}
            return updateStorage({ locale: lCode, ...payload[lCode], place_id: this.detail ? this.detail.id : payload.place_id })
          } catch (error) {
            return () => {}
          }
        })
        await Promise.all(requests)
      } catch (error) {
        //
      }
    },
    async handleUpdateRoomType(payload) {
      try {
        const targetLocales = ['vi', ...this.selectedLocales]
        const requests = targetLocales.map((lCode) => {
          try {
            if (!payload[lCode]) return () => {}
            return updateRoomType({ locale: lCode, ...payload[lCode], place_id: this.detail ? this.detail.id : payload.place_id })
          } catch (error) {
            return () => {}
          }
        })
        await Promise.all(requests)
      } catch (error) {
        //
      }
    },
    handleReloadConstant() {
      this.updateReloadConstant(dayjs().unix())
    }
  }
}
</script>

<style lang="scss" scoped>
.translate-popup {
  .el-input-number {
    width: 100%;
  }
}
.locales-group {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-top: 10px;
}
.locale-checkbox {
  margin-right: 0 !important;
  display: inline-flex;
  align-items: center;
}
.locale-checkbox-content {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.locale-flag {
  width: 24px;
  height: 24px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
}
.locale-name {
  font-size: 13px;
  color: #606266;
}

::v-deep .el-table {
  .row-status-error {
    background-color: #fef0f0 !important;
  }
  .row-status-warning {
    background-color: #fdf6ec !important;
  }
}

.cell-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border-radius: 4px;
  width: 100%;
  box-sizing: border-box;

  &.cell-status-error {
    background-color: #fde2e2;
    color: #f56c6c;
    border: 1px solid #fbc4c4;
    font-weight: 500;
  }

  &.cell-status-warning {
    background-color: #faecd8;
    color: #e6a23c;
    border: 1px solid #f5dab1;
    font-weight: 500;
  }

  .status-icon {
    font-size: 13px;
    flex-shrink: 0;
  }
}
</style>
